import { DateTime } from "luxon";

import type { Dataset } from "./SpendChart";

export type DateUnit = "month" | "quarter" | "year";

/** One series ready to plot: a recharts-safe key paired with the dataset behind it. */
export type Series = { key: string; dataset: Dataset; total: number };

/** One row of the chart: a formatted period plus one number per series key. */
export type ChartRow = { period: string } & Record<string, string | number>;

export const MAX_SERIES = 10;

export const PERIOD_FORMATS: Record<DateUnit, string> = {
  quarter: "Qq yyyy",
  month: "MMM yyyy",
  year: "yyyy",
};

/** Every period start from `min` to `max` inclusive, one `dateUnit` at a time. */
export function periodsBetween(min: DateTime, max: DateTime, dateUnit: DateUnit): DateTime[] {
  const periods: DateTime[] = [];
  for (let date = min; date <= max; date = date.plus({ [dateUnit]: 1 })) {
    periods.push(date);
  }
  return periods;
}

/**
 * Turns a dataset id into something safe to use as a recharts `dataKey` and a CSS
 * custom property name.
 *
 * The id is only a guid when the API returned one; `buildGroupedSpendDatasets`
 * falls back to the group's name, which can be free text. An unescaped name makes
 * `--color-St. Mary's NHS Trust` invalid, so the declaration is dropped, `var()`
 * fails, and the line renders with no stroke at all. The constant prefix also keeps
 * a numeric name from being treated as an array index, which would reorder the
 * config object and hand out the wrong colours.
 */
function toSeriesKey(id: string): string {
  const sanitised = id.replace(/[^\w-]/g, "-").replace(/^-+|-+$/g, "");
  return `s-${sanitised || "unknown"}`;
}

function datasetTotal(dataset: Dataset): number {
  return dataset.data.reduce((sum, point) => sum + point.total, 0);
}

/**
 * Biggest spender first, capped at the number of colours the palette has. A dataset
 * marked `sortLast` follows the rest regardless of size.
 *
 * Order matters twice over: the legend follows it, and the chart config hands out
 * `sysChart1..10` by position to any dataset without its own colour. The callers already
 * request ten, but the cap keeps an eleventh from resolving to a colour token that doesn't
 * exist and rendering blank.
 */
export function buildSeries(data: Dataset[]): Series[] {
  const used = new Set<string>();

  return data
    .map((dataset) => ({ dataset, total: datasetTotal(dataset) }))
    .sort(
      (a, b) => Number(!!a.dataset.sortLast) - Number(!!b.dataset.sortLast) || b.total - a.total,
    )
    .slice(0, MAX_SERIES)
    .map(({ dataset, total }) => {
      const base = toSeriesKey(dataset.id);
      let key = base;
      let suffix = 2;
      while (used.has(key)) {
        key = `${base}-${suffix}`;
        suffix += 1;
      }
      used.add(key);
      return { key, dataset, total };
    });
}

/**
 * Pivots the datasets into the row-per-period shape recharts wants, walking the
 * calendar from the earliest to the latest period so gaps stay visible.
 *
 * The walk matters more here than it did under Chart.js: the x-axis is now a
 * category axis rather than a time scale, so a period nobody spent in would
 * otherwise collapse to nothing instead of showing as a dip.
 *
 * Missing values are 0 rather than undefined, which would break the line into
 * disconnected segments and drop those series out of the tooltip.
 *
 * Points are bucketed to the start of their period and summed, so the totals stay
 * right even when the data is at a finer grain than `dateUnit`, which happens for
 * the moment between changing the date unit and the new data arriving.
 */
export function toChartRows(series: Series[], dateUnit: DateUnit): ChartRow[] {
  const totalsByKey = new Map<string, Map<string, number>>();
  let min: DateTime | undefined;
  let max: DateTime | undefined;

  for (const { key, dataset } of series) {
    const byPeriod = new Map<string, number>();
    for (const point of dataset.data) {
      const date = DateTime.fromISO(point.date);
      if (!date.isValid) {
        continue;
      }
      const period = date.startOf(dateUnit);
      const isoPeriod = period.toISODate() as string;
      byPeriod.set(isoPeriod, (byPeriod.get(isoPeriod) ?? 0) + point.total);
      if (!min || period < min) {
        min = period;
      }
      if (!max || period > max) {
        max = period;
      }
    }
    totalsByKey.set(key, byPeriod);
  }

  if (!min || !max) {
    return [];
  }

  const rows: ChartRow[] = [];
  for (const date of periodsBetween(min, max, dateUnit)) {
    const isoDate = date.toISODate() as string;
    const row: ChartRow = { period: date.toFormat(PERIOD_FORMATS[dateUnit]) };
    for (const { key } of series) {
      row[key] = totalsByKey.get(key)?.get(isoDate) ?? 0;
    }
    rows.push(row);
  }
  return rows;
}

/**
 * Always two decimal places, so the tooltip's ranked values line up in a column.
 * The shared `currencyFormat` uses significant digits instead, which puts
 * "£141.8bn" next to "£41.74bn" and leaves the decimal points ragged.
 */
const SPEND_VALUE_FORMAT = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  notation: "compact",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatSpendValue(value: number): string {
  return SPEND_VALUE_FORMAT.format(value);
}

/** One ungrouped spend-over-time point: total spend and how many transactions made it up. */
export type TrendPoint = { date: string; total: number; transactions: number };

/** One row of the trend chart, plus the period start its click handler filters by. */
export type TrendRow = {
  period: string;
  periodStart: string;
  total: number;
  transactions: number;
};

/**
 * Pivots ungrouped spend points into the rows the trend chart plots: spend and
 * transaction count per period, walking the calendar from the earliest to the latest
 * so a period nobody spent in shows as a dip rather than collapsing.
 *
 * Points are bucketed to the start of their period and summed, so the totals stay right
 * even when the data is at a finer grain than `dateUnit`, which happens for the moment
 * between changing the date unit and the new data arriving.
 *
 * Deliberately separate from `toChartRows` rather than sharing its bucketing: that one
 * pivots an arbitrary number of series into a column each, this one sums two fixed
 * measures, and folding them together means reworking the function the buyer and
 * supplier charts render through. They share the calendar walk and nothing else.
 */
export function toTrendRows(points: TrendPoint[], dateUnit: DateUnit): TrendRow[] {
  const byPeriod = new Map<string, { total: number; transactions: number }>();
  let min: DateTime | undefined;
  let max: DateTime | undefined;

  for (const point of points) {
    const date = DateTime.fromISO(point.date);
    if (!date.isValid) {
      continue;
    }
    const period = date.startOf(dateUnit);
    const isoPeriod = period.toISODate() as string;
    const running = byPeriod.get(isoPeriod) ?? { total: 0, transactions: 0 };
    byPeriod.set(isoPeriod, {
      total: running.total + point.total,
      transactions: running.transactions + point.transactions,
    });
    if (!min || period < min) {
      min = period;
    }
    if (!max || period > max) {
      max = period;
    }
  }

  if (!min || !max) {
    return [];
  }

  return periodsBetween(min, max, dateUnit).map((date) => {
    const isoPeriod = date.toISODate() as string;
    const totals = byPeriod.get(isoPeriod);
    return {
      period: date.toFormat(PERIOD_FORMATS[dateUnit]),
      periodStart: isoPeriod,
      total: totals?.total ?? 0,
      transactions: totals?.transactions ?? 0,
    };
  });
}
